import { useMemo, useCallback } from "react"
import DataIntegrator from "gatherers/data-integrator"
import MultiLineGraph from "renderers/multi-line-graph"
import { createPerformanceCumulativeMultiDateQuery } from "gatherers/multi-date/cumulative-performance"
import { transformPerformanceData, useCurrentPerformanceItems, resolvePerformanceDrilldownSeed } from "connected-components/graph/hooks/use-performance-items"
import { filterItemsWithData } from "connected-components/graph/helpers"
import { useBaseWithSegment, useFilteredSegment } from "connected-components/dashboard/helpers"
import { useThemeChartColors } from "hooks/use-theme-chart-colors"
import { MODEL_METADATA_SHAPE } from "connected-components/asset-view/prop-types"
import { useGraphData } from "../../hooks/use-graph-data"
import { getIntradayChartMode, useIntradayState } from "connected-components/dashboard/hooks/use-intraday-state"
import { useIsRfrAttributionEnabled } from "resources/account"
import { usePanelId } from "connected-components/dashboard/context/panel-id"
import { useDrilldownOpener } from "connected-components/dashboard/drilldown/use-drilldown-opener"
import { buildDrilldownIntent } from "connected-components/dashboard/drilldown/build-drilldown-intent"
import { resolveIntradayDrilldownFields } from "connected-components/dashboard/drilldown/resolve-intraday-drilldown-fields"
import { useGraphTemplateChart } from "../../hooks/use-graph-template-chart"
import { GRAPH_PERFORMANCE_TEMPLATE_KEY, GRAPH_CATEGORY_DEFAULTS } from "../template-chart-config"
import { useIntradayQueryInvalidation } from "connected-components/dashboard/hooks/use-intraday-query-invalidation"
import { isIntradaySimulationEligible } from "gatherers/helpers"

export default function PerformanceGraph({ modelMetadata }) {
  const { startDate, performanceStartDate, performanceEndDate, base, filter, normalization, instrument, comparisonInstrument, secondInstrument, isEndVsStartDateComparison, disabledItems, setDisabledItems, setOnlyItem, enableItems, intradayBatchMetadata, intradayPricingAsOf, instrumentsMetadata, isIntradayMode } = useGraphData()
  const { selectedCategory } = useGraphTemplateChart(GRAPH_PERFORMANCE_TEMPLATE_KEY, GRAPH_CATEGORY_DEFAULTS)
  const [{ lastIntradayBatchId }] = useIntradayState()
  const isRfrAttributionEnabled = useIsRfrAttributionEnabled()
  const panelId = usePanelId()
  const openDrilldown = useDrilldownOpener()

  const baseWithSegment = useBaseWithSegment(base, filter, normalization)
  const segment = useFilteredSegment(filter, normalization)
  const instrumentWithSegment = useMemo(() => ({ ...instrument, segment }), [instrument, segment])

  const categories = useMemo(() => {
    return modelMetadata.categories.map(({ id, label, color }) => ({
      id,
      label,
      color,
    }))
  }, [modelMetadata])

  const factorsMetadata = useMemo(() => {
    return modelMetadata.factors
  }, [modelMetadata])

  const currentItems = useCurrentPerformanceItems({
    categories,
    factorsMetadata,
    disabledItems,
    selectedCategory,
    isRfrAttributionEnabled,
  })

  const chartColors = useThemeChartColors()
  const filterItems = useCallback(
    (items, data) => filterItemsWithData(items, data, chartColors.series),
    [chartColors.series],
  )

  const intradayBatchId = intradayBatchMetadata?.id

  const handleDotClick = useCallback((clickPosition, focusedItemId) => {
    if (!panelId || !focusedItemId) return

    const { selectedCategory: drilldownCategory, selectedFactor, clickedItemId } = resolvePerformanceDrilldownSeed({
      focusedItemId,
      selectedCategory,
      categories,
      factorsMetadata,
    })

    openDrilldown(buildDrilldownIntent({
      panelId,
      sourceSurface: "graph.performance",
      title: "Asset View: Performance",
      view: "performance",
      context: {
        modelId: modelMetadata.id,
        instrument,
        comparisonInstrument,
        secondInstrument,
        base,
        startDate: performanceStartDate,
        endDate: clickPosition?.date ?? performanceEndDate,
        isIntradayMode,
        ...resolveIntradayDrilldownFields(isIntradayMode, intradayBatchMetadata, intradayPricingAsOf),
        filter,
        normalization,
        isEndVsStartDateComparison,
      },
      clickPosition,
      seed: {
        selectedCategory: drilldownCategory,
        selectedFactor,
        clickedDate: clickPosition?.date,
        clickedItemId,
      },
    }))
  }, [panelId, openDrilldown, selectedCategory, categories, factorsMetadata, modelMetadata.id, instrument, comparisonInstrument, secondInstrument, base, performanceStartDate, performanceEndDate, isIntradayMode, intradayBatchMetadata, intradayPricingAsOf, filter, normalization, isEndVsStartDateComparison])

  const gatherers = useMemo(() => {
    return [createPerformanceCumulativeMultiDateQuery(
      modelMetadata.id,
      instrumentWithSegment,
      baseWithSegment,
      performanceStartDate,
      performanceEndDate,
      isIntradayMode,
      { isRfrAttributionEnabled, batchId: intradayBatchId, lastIntradayBatchId, intradayEnabled: instrumentsMetadata?.original?.intradayEnabled },
    )]
  }, [modelMetadata, instrumentWithSegment, performanceStartDate, performanceEndDate, baseWithSegment, isIntradayMode, isRfrAttributionEnabled, intradayBatchId, lastIntradayBatchId, instrumentsMetadata])

  useIntradayQueryInvalidation(gatherers, intradayBatchMetadata, performanceStartDate, performanceEndDate, intradayPricingAsOf, lastIntradayBatchId)

  const isUsingIntradayQuery = isIntradaySimulationEligible(isIntradayMode, performanceStartDate, performanceEndDate, intradayBatchId, lastIntradayBatchId)
  // Never batchTrend — ModelIntradaySimulationBatch has no performance field — but the tooltip
  // still needs liveTick to keep its latest-price time header.
  const intradayChartMode = getIntradayChartMode(false, isUsingIntradayQuery)
  const noDataMessage = isUsingIntradayQuery ? "Intraday performance data isn't available yet." : undefined

  return <DataIntegrator
    dataTransform={data => transformPerformanceData(data, categories)}
    metrics={gatherers}
    Renderer={MultiLineGraph}
    rendererProps={{
      items: currentItems,
      filterItems,
      endDate: performanceEndDate,
      startDate,
      setItems: setDisabledItems,
      setOnlyItem,
      enableItems,
      loadingMessage: "Loading performance data...",
      errorMessage: "Failed to load performance data. Please try again.",
      emptyMessage: "No performance data available.",
      onDotClick: handleDotClick,
      noDataMessage,
      isOneDayMode: isUsingIntradayQuery,
      intradayChartMode,
    }}
  />
}

PerformanceGraph.propTypes = {
  modelMetadata: MODEL_METADATA_SHAPE.isRequired,
}
